import React from 'react'
import I18n from 'i18n'
import moment from 'moment'
import { reduce, last, maxBy, get, find } from 'lodash'

import { isDailyQuestionnaire, isQuestionnaire, isTrainingLog, readDailyQuestionnaire, readTrainingQuestionnaire } from '../../../common/utils/QuestionnaireUtils'
import { dataTypes, EMPTY_RECORD, sdvDataFormatSports } from '../../../common/Constants'
import { ms2kmh, s2hms } from '../../../common/Units'
import { getFirstDataRow, roundOrEmpty } from '../../../common/Utils'
import { getTrainingLoad } from '../../../common/Calculations'
import UserProfileTableEntry from '../../../common/profile_representations/UserProfileTableEntry'
import VDO from 'components/common/VDO'

const getZScoreColor = (zScore) => {
  if (zScore === null || zScore === undefined || !isFinite(zScore)) return 'z-score-green'

  const numericZScore = Number(zScore)

  if (numericZScore < 1) return 'z-score-green'
  if (numericZScore >= 1 && numericZScore <= 2) return 'z-score-yellow'
  if (numericZScore > 2) return 'z-score-orange'

  return 'z-score-green'
}

const ZScoreColoredWrapper = (props) => {
  const { value, zScoreEnabled, zScore, children } = props

  if (!zScoreEnabled || value === undefined || value === EMPTY_RECORD || value === null) {
    return <span>{children}</span>
  }

  const color = getZScoreColor(zScore)
  if (!color) return <span>{children}</span>

  const backgroundClass = color.replace('z-score-', 'z-score-bg-')

  return (
    <span
      className={backgroundClass} style={{
        display: 'block',
        width: '100%',
        height: '100%',
        padding: '8px',
        margin: '-8px'
      }}
    >
      {children}
    </span>
  )
}

const getHealthBackgroundClass = (isSick, isInjured) => {
  if (isSick === undefined && isInjured === undefined) {
    return ''
  }

  return (isSick || isInjured) ? 'health-bg-sick-injured' : 'health-bg-healthy'
}

const getHealthProblems = (dailyData) => {
  const sickStatus = dailyData?.isSick
  const injuryStatus = dailyData?.isInjured

  if (sickStatus === undefined && injuryStatus === undefined) {
    return EMPTY_RECORD
  }

  const healthProblems = []
  if (sickStatus) { healthProblems.push(I18n.t('components.dashboards.coach_individual.health.sick')) }
  if (injuryStatus) { healthProblems.push(I18n.t('components.dashboards.coach_individual.health.injured')) }
  if (healthProblems.length === 0) { healthProblems.push(I18n.t('components.dashboards.coach_individual.health.healthy')) }

  return healthProblems.join(' & ')
}

const getSleepDurationString = (hours, minutes) => {
  const duration = moment.duration({ hours, minutes })
  if (!hours || !minutes) {
    return EMPTY_RECORD
  } else {
    return `${parseInt(duration.asHours())}${I18n.t('units.h')}${duration.minutes()}${I18n.t('units.min')}`
  }
}

export const prepareDailyData = (readDailyData) => {
  const preparedDailyData = { ...readDailyData }
  preparedDailyData.healthProblems = getHealthProblems(readDailyData)

  return preparedDailyData
}

export const prepareTrainingLogData = (readTrainingLog) => {
  const preparedLogData = { ...readTrainingLog }
  const { rpe, duration, training_type: trainingType, session_type: sessionType } = preparedLogData

  preparedLogData.training_type = trainingType ? I18n.t(trainingType) : EMPTY_RECORD
  preparedLogData.session_type = sessionType ? I18n.t(sessionType) : EMPTY_RECORD

  const trainingLoadValue = getTrainingLoad(rpe, duration)
  preparedLogData.srpe = roundOrEmpty(trainingLoadValue)
  preparedLogData.duration = `${s2hms(duration)} ${I18n.t('units.h')}`
  return preparedLogData
}

export const findScheduledTraining = (scheduledTrainings, athleteId, day = null) => {
  if (!scheduledTrainings || !Array.isArray(scheduledTrainings)) {
    return null
  }

  return find(scheduledTrainings, (schedule) => {
    // Check if this schedule is for the given athlete
    const isForAthlete = schedule?.athletes?.profiles?.some(profile => profile.id === athleteId)

    if (day) {
      const isForDay = moment(schedule.start_date).isSame(moment(day), 'day')
      return isForAthlete && isForDay
    }

    return isForAthlete
  })
}

export const createAthleteRowEntry = (athleteProfile, athleteDatasets, onRowClick = () => {}, directScheduledTrainings = null, currentDay = null, zScoreColoringEnabled = true) => {
  const rowEntry = {
    handleClick: () => {},
    content: {}
  }
  let { dailyQuestionnaire, trainingLog, activity, scheduledTraining, extraData, preComputedZScores } = athleteDatasets

  if (directScheduledTrainings && athleteProfile?.id) {
    const directScheduledTraining = findScheduledTraining(directScheduledTrainings, athleteProfile.id)
    if (directScheduledTraining) {
      scheduledTraining = directScheduledTraining
    }
    // If backend logic is added in the future, this can be removed.
  }

  const parsedDailyData = prepareDailyData(readDailyQuestionnaire(getFirstDataRow(dailyQuestionnaire)?.values))
  const parsedTrainingData = prepareTrainingLogData(readTrainingQuestionnaire(getFirstDataRow(trainingLog)?.values))
  const expectedRPE = scheduledTraining?.expected_rpe
  const avgSpeed = ms2kmh(activity?.summary?.time_series_summaries?.speed?.mean)
  const avgPower = activity?.summary?.time_series_summaries?.power?.mean
  const duration = activity?.summary?.duration || parsedTrainingData?.duration
  const elapsedTimeHrZones = activity?.summary?.time_series_summaries?.hr?.zone_elapsed_seconds

  const { content } = rowEntry

  content.athlete = <UserProfileTableEntry profile={athleteProfile} />

  content.healthStatus = (
    <span
      className={zScoreColoringEnabled ? getHealthBackgroundClass(parsedDailyData?.isSick, parsedDailyData?.isInjured) : ''}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        padding: '8px',
        margin: '-8px'
      }}
    >
      {parsedDailyData?.healthProblems}
    </span>
  )

  // Look up pre-computed z-score from backend response
  const getPreComputedZScore = (fieldName) => {
    if (!preComputedZScores) return null
    return preComputedZScores[fieldName]?.z_score ?? null
  }

  // Create z-score colored content using pre-computed z-scores from the backend
  const createZScoreContent = (value, fieldName, displayValue = value) => {
    if (!zScoreColoringEnabled) {
      return displayValue
    }

    // Early exit if value is undefined or empty
    if (value === undefined || value === EMPTY_RECORD || value === null || isNaN(value)) {
      return displayValue
    }

    const zScore = getPreComputedZScore(fieldName)

    return (
      <ZScoreColoredWrapper
        value={value}
        zScoreEnabled={zScoreColoringEnabled}
        zScore={zScore}
      >
        {displayValue}
      </ZScoreColoredWrapper>
    )
  }

  content.restingHr = createZScoreContent(parsedDailyData?.restingHr, 'restingHr', roundOrEmpty(parsedDailyData?.restingHr)) || roundOrEmpty(parsedDailyData?.restingHr)

  const wellnessDisplay = parsedDailyData.wellnessScore ? `${roundOrEmpty(parsedDailyData.wellnessScore)}%` : undefined
  content.wellbeingScore = createZScoreContent(parsedDailyData.wellnessScore, 'wellnessScore', wellnessDisplay) || wellnessDisplay

  const sleepDisplay = getSleepDurationString(parsedDailyData?.sleepDuration?.hours, parsedDailyData?.sleepDuration?.minutes)

  const sleepValue = (parsedDailyData?.sleepDuration?.hours !== undefined && parsedDailyData?.sleepDuration?.minutes !== undefined)
    ? (parsedDailyData.sleepDuration.hours || 0) + ((parsedDailyData.sleepDuration.minutes || 0) / 60)
    : undefined
  content.sleepDuration = createZScoreContent(sleepValue, 'sleepDurationHours', sleepDisplay) || sleepDisplay

  const sleepQualityDisplay = roundOrEmpty(parsedDailyData?.sleepQuality)
  content.sleepQuality = createZScoreContent(parsedDailyData?.sleepQuality, 'sleepQuality', sleepQualityDisplay) || sleepQualityDisplay

  const readinessDisplay = roundOrEmpty(parsedDailyData?.readiness)
  content.readiness = createZScoreContent(parsedDailyData?.readiness, 'readiness', readinessDisplay) || readinessDisplay

  const stressDisplay = roundOrEmpty(parsedDailyData?.stress)
  content.stress = createZScoreContent(parsedDailyData?.stress, 'stress', stressDisplay) || stressDisplay

  const moodDisplay = roundOrEmpty(parsedDailyData?.mood)
  content.mood = createZScoreContent(parsedDailyData?.mood, 'mood', moodDisplay) || moodDisplay

  const fatigueDisplay = roundOrEmpty(parsedDailyData?.fatigue)
  content.fatigue = createZScoreContent(parsedDailyData?.fatigue, 'fatigue', fatigueDisplay) || fatigueDisplay

  const musclePainDisplay = roundOrEmpty(parsedDailyData?.generalMusclePain)
  content.musclePain = createZScoreContent(parsedDailyData?.generalMusclePain, 'generalMusclePain', musclePainDisplay) || musclePainDisplay

  // Health & Wellbeing fields - ALL get z-score coloring
  const rpeDisplay = roundOrEmpty(parsedTrainingData?.rpe)

  // Handle expected RPE display while preserving z-score coloring
  if (expectedRPE) {
    const rpe = parsedTrainingData?.rpe ? `${parsedTrainingData?.rpe}` : EMPTY_RECORD
    const rpeWithExpected = `${rpe} (${I18n.t('components.dashboards.coach_individual.scheduled')}: ${expectedRPE})`
    content.rpe = createZScoreContent(parsedTrainingData?.rpe, 'rpe', rpeWithExpected) || rpeWithExpected
  } else {
    content.rpe = createZScoreContent(parsedTrainingData?.rpe, 'rpe', rpeDisplay) || rpeDisplay
  }

  const srpeValue = parsedTrainingData?.srpe
  const srpeDisplay = roundOrEmpty(srpeValue)
  content.srpe = createZScoreContent(srpeValue, 'srpe', srpeDisplay) || srpeDisplay

  const acwrDisplay = roundOrEmpty(extraData?.acwr)
  content.acwr = createZScoreContent(extraData?.acwr, 'acwr', acwrDisplay) || acwrDisplay

  const trimpValue = calculateTrimp(elapsedTimeHrZones)
  const trimpDisplay = roundOrEmpty(trimpValue)
  content.trimp = createZScoreContent(trimpValue, 'trimp', trimpDisplay) || trimpDisplay

  const satisfactionDisplay = roundOrEmpty(parsedTrainingData?.satisfaction)
  content.satisfaction = createZScoreContent(parsedTrainingData?.satisfaction, 'satisfaction', satisfactionDisplay) || satisfactionDisplay
  const funDisplay = roundOrEmpty(parsedTrainingData?.fun)
  content.fun = createZScoreContent(parsedTrainingData?.fun, 'fun', funDisplay) || funDisplay
  content.comments = parsedTrainingData?.comments || EMPTY_RECORD
  content.coachComments = scheduledTraining?.coach_comments || EMPTY_RECORD
  // Health & Wellbeing load metrics - z-score coloring
  const speedLoadValue = getTrainingLoad(avgSpeed, duration)
  const speedLoadDisplay = roundOrEmpty(speedLoadValue)
  content.speedLoad = createZScoreContent(speedLoadValue, 'speedLoad', speedLoadDisplay) || speedLoadDisplay

  const powerLoadValue = getTrainingLoad(avgPower, duration)
  const powerLoadDisplay = roundOrEmpty(powerLoadValue)
  content.powerLoad = createZScoreContent(powerLoadValue, 'powerLoad', powerLoadDisplay) || powerLoadDisplay

  rowEntry.handleClick = () => { return onRowClick(athleteProfile) }

  return rowEntry
}

export const isSportsActivity = (vdo) => {
  return Object.values(sdvDataFormatSports).includes(vdo?.metadatum?.sport?.name) ||
    vdo?.data_type?.data_type === dataTypes.strava_type ||
    vdo?.data_type?.data_type === dataTypes.fit_data_type
}

export const isCorrespondingSchedule = (schedule, trainingLog) => {
  const trainingTime = trainingLog?.metadatum?.event_start // TODO Retrive the time of the activity if the training log does not exist
  return schedule?.athlete?.id === trainingLog?.owner?.id && moment(schedule.start_date).isSame(trainingTime, 'hour')
}

// TRIMP = SUM( minutesInZone1 * 1, minutesInZone2 * 2, ..., minutesInZone5 * 5)
export const calculateTrimp = (elapsedTimePerZone) => {
  if (!elapsedTimePerZone) {
    return null
  }

  const elapsedTimesWithoutZone0 = elapsedTimePerZone?.slice(1)
  const zonesMinutesCount = elapsedTimesWithoutZone0.map((seconds) => seconds / 60)

  const trimp = reduce(zonesMinutesCount, (sum, count, index) => {
    sum += count * (index + 1)
    return sum
  }, 0)

  return trimp
}

export const fetchUserData = (backend, groupId, profileId, start, end, pageNumber = 1, userDatasets = []) => {
  if (!groupId) {
    return Promise.resolve([])
  }

  const isoStart = start.toISOString()
  const isoEnd = end.toISOString()

  // Caller of this function is responsible for catching errors
  return backend.groups.recentActivity(groupId, pageNumber, profileId ? [profileId] : profileId, isoStart, isoEnd).then((recentDataResponse) => {
    const { has_next: hasNext, next_page: nextPage } = recentDataResponse.data.page
    userDatasets.push(...recentDataResponse.data?.data)
    if (hasNext) {
      return fetchUserData(backend, groupId, profileId, start, end, nextPage, userDatasets)
    } else {
      return userDatasets
    }
  })
}

// TODO Change backend. The endpoint should NOT need a collectionType
export const fetchUserAcwr = (backend, profileId, day, collectionType) => {
  return backend.dashboards.trends.acwr(collectionType, day, day, undefined, undefined, [profileId])
    .then((aggregatedResults) => {
      const acwrValues = aggregatedResults?.data?.acwr
      if (acwrValues) {
        const mostRecentAcwr = last(acwrValues)
        return Promise.resolve(mostRecentAcwr)
      } else {
        return Promise.reject(aggregatedResults)
      }
    })
}

// Fetches schedules created by the coach
export const fetchUserSchedule = (backend, day) => {
  return backend.schedules.index(day).then((weekSchedulesResponse) => {
    return Promise.resolve(weekSchedulesResponse?.data?.data)
  })
}

// Adds the first dataset of each type to the data object
export const addFirstDatasetOfEachType = (data, datasets) => {
  data.activity = find(datasets, (vdo) => isSportsActivity(vdo))

  const questionnaires = datasets.filter((vdo) => isQuestionnaire(vdo))
  data.trainingLog = find(questionnaires, (vdo) => isTrainingLog(VDO.sdo(vdo)))
  data.dailyQuestionnaire = find(questionnaires, (vdo) => isDailyQuestionnaire(VDO.sdo(vdo)))
}

// Adds the first dataset of each type to the data object
export const addFirstDatasetOfEachTypePerProfile = (data, datasets) => {
  // get all profile ids of the datasets
  const profileIds = datasets.map((vdo) => vdo.owner.id)
  // iterate over the profile ids and add the first dataset of each type to the data object
  profileIds.forEach((profileId) => {
    const profileDatasets = datasets.filter((vdo) => vdo.owner.id === profileId)
    data[profileId] = {}
    data[profileId].activity = find(profileDatasets, (vdo) => isSportsActivity(vdo))

    const questionnaires = profileDatasets.filter((vdo) => isQuestionnaire(vdo))
    data[profileId].trainingLog = find(questionnaires, (vdo) => isTrainingLog(VDO.sdo(vdo)))
    data[profileId].dailyQuestionnaire = find(questionnaires, (vdo) => isDailyQuestionnaire(VDO.sdo(vdo)))
  })
}

// Fetches datasets of a certain day and adds the first of each type to the data object
export const willAddDatasetsOfChosenDay = (data, backend, groupId, profileId, day) => {
  const beginningOfDay = moment(day).startOf('day')
  const endOfDay = moment(day).endOf('day')

  return fetchUserData(backend, groupId, profileId, beginningOfDay, endOfDay).then((datasets) => {
    addFirstDatasetOfEachType(data, datasets)
    return Promise.resolve(data)
  })
}

// Fetches the acwr data of a certain day and adds it to the data object
export const willAddAcwr = (data, backend, profileId, day) => {
  const collectionType = data?.activity?.summary?.collection_type // TODO This should not be needed in the future once all datasets have a collectionType
  if (!collectionType) {
    return Promise.resolve(data)
  }

  return fetchUserAcwr(backend, profileId, day, collectionType).then((acwr) => {
    data.extraData = { acwr }
    return Promise.resolve(data)
  })
}

export const getFastestLap = (laps) => {
  if (!laps || laps?.length <= 0) {
    return null
  }

  return maxBy(laps, (lap) => get(lap, 'summaries.speed.mean', null)) || null
}
